
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import SwitchToggle from '../Generics/SwitchToggle.vue';
import {
  DecimalPointInput,
  DmsPointInput,
  LatHemisphere,
  LatLngPoint,
  LngHemisphere,
  parseDecimalPoint,
  parseDmsPoint,
  POINT_SEARCH_ERROR_MESSAGES,
  pointToDms,
  splitPastedLatLng
} from '../../utils/point-coordinates';

type DmsAxis = 'lat' | 'lng';
type DmsPart = 'degrees' | 'minutes' | 'seconds';

const emptyDecimal = (): DecimalPointInput => ({ lat: '', lng: '' });

/** Hemisferios por defecto: sur y oeste, donde están los lotes que se cargan. */
const emptyDms = (): DmsPointInput => ({
  lat: { hemisphere: LatHemisphere.S, degrees: '', minutes: '', seconds: '' },
  lng: { hemisphere: LngHemisphere.W, degrees: '', minutes: '', seconds: '' }
});

/** Valores de un GMS como texto (los campos editan strings). */
const dmsAsText = (value: DmsPointInput): DmsPointInput => ({
  lat: { ...value.lat, degrees: String(value.lat.degrees), minutes: String(value.lat.minutes), seconds: String(value.lat.seconds) },
  lng: { ...value.lng, degrees: String(value.lng.degrees), minutes: String(value.lng.minutes), seconds: String(value.lng.seconds) }
});

/**
 * "Buscar por punto (lat/lng)": carga de un punto en decimal o en grados, minutos y segundos (GMS).
 * Presentacional: no conoce el mapa ni inyecta nada; el padre centra el mapa con lo que emite.
 *
 * - `search(point)`: punto válido (Centrar o Enter). Los errores se muestran debajo de los campos.
 * - `clear()`: Limpiar. `close()`: la ×; en modo `compact` además se vuelve a plegar al ícono.
 */
@Component({
  components: { SwitchToggle }
})
export default class PointSearch extends Vue {
  static readonly DEFAULT_PANEL_WIDTH = 320;
  static readonly DMS_PANEL_MIN_WIDTH = 420;

  /** Plegado en un ícono (`mdi-crosshairs-gps`) que se despliega al hacer clic. */
  // `type: Boolean` es obligatorio: se usa como atributo suelto (`<PointSearch compact />`) y sin él Vue lo pasa como
  // "" (falso), porque el proyecto no emite metadata de decoradores para que vue-property-decorator deduzca el tipo.
  @Prop({ type: Boolean, default: false }) readonly compact!: boolean;
  /**
   * Ancho del panel en formato decimal (p. ej. el de la barra del centroide, para que queden alineados). Sin valor →
   * `DEFAULT_PANEL_WIDTH`. En GMS el panel es más ancho (`DMS_PANEL_MIN_WIDTH`) para que se lean los placeholders.
   */
  @Prop({ default: null }) readonly width!: number | null;
  /**
   * Punto marcado en el mapa (lo recuerda el padre: el componente puede recrearse con el mapa). En `compact`, el ícono
   * plegado se resalta y su tooltip muestra las coordenadas.
   */
  @Prop({ default: null }) readonly markedPoint!: LatLngPoint | null;

  expanded: boolean = false;
  isDms: boolean = false;
  decimal: DecimalPointInput = emptyDecimal();
  dms: DmsPointInput = emptyDms();
  error: string = '';

  readonly dmsAxes: Array<{ key: DmsAxis; label: string; hemispheres: string[] }> = [
    { key: 'lat', label: 'Latitud', hemispheres: [LatHemisphere.N, LatHemisphere.S] },
    { key: 'lng', label: 'Longitud', hemispheres: [LngHemisphere.E, LngHemisphere.W] }
  ];

  readonly dmsParts: Array<{ key: DmsPart; label: string }> = [
    { key: 'degrees', label: 'Grados' },
    { key: 'minutes', label: 'Minutos' },
    { key: 'seconds', label: 'Segundos' }
  ];

  /** Al cambiar de formato se convierte lo cargado; si no es válido, el otro formato queda vacío. */
  get hasMarkedPoint(): boolean {
    return !!this.markedPoint;
  }

  get markedPointText(): string {
    return this.markedPoint ? `${this.markedPoint.lat.toFixed(6)}, ${this.markedPoint.lng.toFixed(6)}` : '';
  }

  /** Decimal: el ancho pedido (o el por defecto). GMS: al menos `DMS_PANEL_MIN_WIDTH`. */
  get panelWidth(): number {
    const base = this.width && this.width > 0 ? Math.round(this.width) : PointSearch.DEFAULT_PANEL_WIDTH;
    return this.isDms ? Math.max(base, PointSearch.DMS_PANEL_MIN_WIDTH) : base;
  }

  @Watch('isDms')
  onFormatChange(isDms: boolean) {
    this.error = '';
    if (isDms) {
      const result = parseDecimalPoint(this.decimal);
      this.dms = result.ok ? dmsAsText(pointToDms(result.point)) : emptyDms();
    } else {
      const result = parseDmsPoint(this.dms);
      this.decimal = result.ok ? { lat: String(result.point.lat), lng: String(result.point.lng) } : emptyDecimal();
    }
  }

  /** Pegar "lat, lng" en Latitud lo separa en los dos campos (regla en `splitPastedLatLng`). */
  onDecimalLatInput(value: string) {
    this.error = '';
    const pasted = splitPastedLatLng(value);
    if (pasted) {
      this.decimal = { lat: pasted[0], lng: pasted[1] };
    } else {
      this.decimal.lat = value;
    }
  }

  submit() {
    const result = this.isDms ? parseDmsPoint(this.dms) : parseDecimalPoint(this.decimal);
    if (!result.ok) {
      this.error = POINT_SEARCH_ERROR_MESSAGES[result.error];
      return;
    }
    this.error = '';
    this.emitSearch(result.point);
    // Plegado: después de centrar se vuelve al ícono para no tapar el mapa (el punto queda marcado).
    if (this.compact) {
      this.expanded = false;
    }
  }

  /** Pliega el panel al ícono sin quitar el punto (solo `compact`). Los valores cargados se conservan. */
  collapse() {
    this.error = '';
    this.expanded = false;
  }

  clear() {
    this.decimal = emptyDecimal();
    this.dms = emptyDms();
    this.error = '';
    this.$emit('clear');
  }

  close() {
    this.error = '';
    this.expanded = false;
    this.$emit('close');
  }

  emitSearch(point: LatLngPoint) {
    this.$emit('search', point);
  }
}
